import { useState } from "react";
import {
  Autocomplete,
  Box,
  FormControl,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { SelectedEquipmentKitType } from "../../../types/equipment.type";
import Loading from "../../../components/shared/layout/Loading";
import {
  useEquipmentUsageBySicId,
  useGetEquipmentBySic,
} from "../../../hooks/react-query/useEquipments";
import { FlexRow } from "../../../components/shared/layout/Flex";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";
import { DeliveryEquipment } from "../../../types/deliveryEquipment.type";
import { useDateSearchParamOrFallbackToToday } from "../../../hooks/useDateSearchParamOrFallbackToToday";
import { dateToInt } from "../../../utils/dateTimeHelper";
import { AutocompleteAvailabilityOption } from "../../../components/shared/AutocompleteAvailabilityOption";
import { useIsFeatureFlagEnabled } from "../../../featureFlags/useIsFeatureFlagEnabled";
import { EquipmentDropdown } from "../../../components/shared/EquipmentDropdown";
import { transformBackendResponse } from "../../../components/shared/equipmentDropdown.utils";
import {
  getStraightTruckOptions,
  getTractorOptions,
  getTrailerOptions,
} from "./equipmentUtils";

type EquipmentSelectionProps = {
  trailerId: number | null;
  tractorId: number | null;
  straightTruckId: number | null;
  setTrailerValue: (value: DeliveryEquipment | null) => void;
  setStraightTruckValue: (value: DeliveryEquipment | null) => void;
  setTractorValue?: (value: DeliveryEquipment | null) => void;
};

export const EquipmentSelection = ({
  trailerId,
  tractorId,
  straightTruckId,
  setTrailerValue,
  setStraightTruckValue,
  setTractorValue,
}: EquipmentSelectionProps) => {
  const [selectedDate] = useDateSearchParamOrFallbackToToday();
  const planDate = dateToInt(selectedDate);

  const [selectedEquipmentType, setSelectedEquipmentType] =
    useState<SelectedEquipmentKitType | null>(null);

  const [serviceCenter] = useSelectedServiceCenter();

  const isNewDropdownEnabled = useIsFeatureFlagEnabled(
    "operations-5722-listing-equipments-dropdown-client"
  );

  const {
    data: equipments = {
      trailers: [],
      straightTrucks: [],
      tractors: [],
    },
    isLoading: equipmentIsLoading,
  } = useEquipmentUsageBySicId(
    serviceCenter.id,
    planDate,
    !isNewDropdownEnabled
  );

  const { data: equipmentBySic, isLoading: equipmentBySicIsLoading } =
    useGetEquipmentBySic({
      sicId: serviceCenter.id,
      EquipmentType: [1, 2, 3],
      planDate,
      enabled: isNewDropdownEnabled,
    });

  const allOptions = equipmentBySic
    ? transformBackendResponse({
        ...equipmentBySic,
        assigned: [...equipmentBySic.assigned, ...equipmentBySic.loadAndHold],
        loadAndHold: [],
      })
    : [];
  const trailerOptionsBySic = allOptions.filter(
    (o) => o.rawData.equipmentType.id === 2
  );
  const tractorOptionsBySic = allOptions.filter(
    (o) => o.rawData.equipmentType.id === 1
  );
  const straightTruckOptionsBySic = allOptions.filter(
    (o) => o.rawData.equipmentType.id === 3
  );

  const trailerOptions = getTrailerOptions(equipments.trailers);
  const tractorOptions = getTractorOptions(equipments.tractors);
  const straightTruckOptions = getStraightTruckOptions(
    equipments.straightTrucks
  );

  const handleSelectEquipmentType = (type: SelectedEquipmentKitType) => {
    setTrailerValue(null);
    setStraightTruckValue(null);
    setTractorValue?.(null);

    setSelectedEquipmentType(type === selectedEquipmentType ? null : type);
  };

  const isLoading = isNewDropdownEnabled
    ? equipmentBySicIsLoading
    : equipmentIsLoading;

  if (isLoading) {
    return <Loading />;
  }

  return (
    <FlexRow>
      <FormControl fullWidth={true}>
        <Typography fontSize={14} fontWeight={600} mb={1}>
          Select an equipment type
        </Typography>
        <Box sx={{ margin: "1px 1px 1px 1px" }}>
          <ToggleButtonGroup
            color="primary"
            value={selectedEquipmentType}
            exclusive
            aria-label="Platform"
            size="small"
          >
            <ToggleButton
              value="Trailer"
              onChange={() => handleSelectEquipmentType("Trailer")}
            >
              Trailer
            </ToggleButton>
            <ToggleButton
              value="Truck"
              onChange={() => handleSelectEquipmentType("Truck")}
            >
              Straight Truck
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        {selectedEquipmentType === "Trailer" && (
          <Box
            mt={1}
            display="flex"
            flexDirection="column"
            gap={1}
            role="tabpanel"
          >
            {isNewDropdownEnabled ? (
              <>
                <EquipmentDropdown
                  label="Trailer #"
                  equipmentTypeName="Trailer"
                  options={trailerOptionsBySic}
                  showSicCode={false}
                  value={
                    trailerOptionsBySic.find((o) => o.id === trailerId) ?? null
                  }
                  onChange={(option) =>
                    setTrailerValue(
                      option
                        ? {
                            id: option.id,
                            name: option.refNumber,
                            equipmentType: option.rawData.equipmentType.id,
                          }
                        : null
                    )
                  }
                />
                {setTractorValue && (
                  <EquipmentDropdown
                    label="Tractor #"
                    equipmentTypeName="Tractor"
                    options={tractorOptionsBySic}
                    showSicCode={false}
                    value={
                      tractorOptionsBySic.find((o) => o.id === tractorId) ??
                      null
                    }
                    onChange={(option) =>
                      setTractorValue(
                        option
                          ? {
                              id: option.id,
                              name: option.refNumber,
                              equipmentType: option.rawData.equipmentType.id,
                            }
                          : null
                      )
                    }
                  />
                )}
              </>
            ) : (
              <>
                <Autocomplete
                  value={
                    trailerOptions.find((d) => Number(d.value) === trailerId) ??
                    null
                  }
                  options={trailerOptions}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setTrailerValue({
                        id: newValue.value,
                        name: newValue.name,
                        equipmentType: newValue.equipmentType,
                      });
                    } else {
                      setTrailerValue(null);
                    }
                  }}
                  getOptionLabel={(trailer) => trailer.name}
                  renderInput={(params) => (
                    <TextField {...params} label="Trailer #" />
                  )}
                  groupBy={(option) => option.group}
                  renderOption={(props, option) => (
                    <AutocompleteAvailabilityOption
                      key={option.name}
                      listItemProps={props}
                      name={option.name}
                      assignment={option.assignment}
                    />
                  )}
                />
                {setTractorValue && (
                  <Autocomplete
                    value={
                      tractorOptions.find(
                        (d) => Number(d.value) === tractorId
                      ) ?? null
                    }
                    options={tractorOptions}
                    onChange={(_, newValue) => {
                      if (newValue) {
                        setTractorValue({
                          id: newValue.value,
                          name: newValue.name,
                          equipmentType: newValue.equipmentType,
                        });
                      } else {
                        setTractorValue(null);
                      }
                    }}
                    getOptionLabel={(tractor) => tractor.name}
                    renderInput={(params) => (
                      <TextField {...params} label="Tractor #" />
                    )}
                    groupBy={(option) => option.group}
                    renderOption={(props, option) => (
                      <AutocompleteAvailabilityOption
                        key={option.name}
                        listItemProps={props}
                        name={option.name}
                        assignment={option.assignment}
                      />
                    )}
                  />
                )}
              </>
            )}
          </Box>
        )}
        {selectedEquipmentType === "Truck" && (
          <Box mt={1} role="tabpanel">
            {isNewDropdownEnabled ? (
              <EquipmentDropdown
                label="ST #"
                equipmentTypeName="Straight Truck"
                showSicCode={false}
                options={straightTruckOptionsBySic}
                value={
                  straightTruckOptionsBySic.find(
                    (o) => o.id === straightTruckId
                  ) ?? null
                }
                onChange={(option) =>
                  setStraightTruckValue(
                    option
                      ? {
                          id: option.id,
                          name: option.refNumber,
                          equipmentType: option.rawData.equipmentType.id,
                        }
                      : null
                  )
                }
              />
            ) : (
              <Autocomplete
                value={
                  straightTruckOptions.find(
                    (d) => Number(d.value) === straightTruckId
                  ) ?? null
                }
                options={straightTruckOptions}
                onChange={(_, newValue) => {
                  if (newValue) {
                    setStraightTruckValue({
                      id: newValue.value,
                      name: newValue.name,
                      equipmentType: newValue.equipmentType,
                    });
                  } else {
                    setStraightTruckValue(null);
                  }
                }}
                getOptionLabel={(straightTruck) => straightTruck.name}
                renderInput={(params) => <TextField {...params} label="ST #" />}
                groupBy={(option) => option.group}
                renderOption={(props, option) => (
                  <AutocompleteAvailabilityOption
                    key={option.name}
                    listItemProps={props}
                    name={option.name}
                    assignment={option.assignment}
                  />
                )}
              />
            )}
          </Box>
        )}
      </FormControl>
    </FlexRow>
  );
};
